/**
 * The `iqz-ddds:` postMessage protocol, from the frame's side. Section 14.
 *
 * Every rule in this file exists because the widget runs inside a page we do not control, and the
 * consequence of getting any of them wrong is a working credential handed to whoever asked:
 *
 *   - **`event.origin` is checked against an allowlist, in both directions.** A `message` handler runs for
 *     every message the frame receives, including from scripts on the host page that have nothing to do
 *     with us, and including from a page that framed us without permission. Without the check, `init` is a
 *     way to make the widget adopt any token - or, worse, to make it send ours somewhere.
 *   - **Nothing is ever posted to `"*"`.** Section 14 says so explicitly. A wildcard target means the
 *     message is delivered to whatever is actually in the parent slot, which is not necessarily the origin
 *     that loaded us.
 *   - **Payloads are validated by the shared zod schemas.** A message whose `type` matches and whose shape
 *     does not is a bug in the host or an attempt, and either way it is not something to interpret
 *     leniently.
 *
 * The allowlist arrives at runtime rather than being compiled in, because a whitelabel image cannot know
 * a customer's origins - see `config.ts`.
 */
import { hostMessageSchema, WIDGET_TO_HOST, type HostMessage, type WidgetMessage } from '@iqz-ddds/contracts';

/**
 * Compare an origin against the allowlist.
 *
 * Exact string equality on the serialised origin, deliberately. No prefix matching and no suffix
 * matching: `https://evil-myprimerica.com` starts with nothing useful and ends with the same characters
 * as the real thing, and every allowlist bug in this shape has been a `startsWith` or an `endsWith`.
 */
export function isAllowedOrigin(origin: string, allowed: readonly string[]): boolean {
  if (origin === '' || origin === 'null') return false;
  return allowed.includes(origin);
}

/**
 * The origin that loaded this frame, when the browser will say.
 *
 * `document.referrer` is the only thing available: `window.parent.origin` throws cross-origin by design,
 * and `document.ancestorOrigins` is WebKit-only. A referrer can be suppressed by the host's own policy,
 * in which case this returns undefined and the caller falls back to accepting `init` from any allowlisted
 * origin - which is still an allowlist, just not a single-origin one.
 *
 * Used to seed the initial outbound target and for nothing else since DG.38: an accepted `init`'s own
 * origin replaces it, so anything that resolves a destination from here instead goes silent under
 * `Referrer-Policy: no-referrer` while the rest of the handshake works. `useReportedHeight` did exactly
 * that, and the panel it left was an unsized iframe.
 */
export function framingOrigin(): string | undefined {
  if (document.referrer === '') return undefined;
  try {
    return new URL(document.referrer).origin;
  } catch {
    return undefined;
  }
}

/** A validated host message, or null when it is not one of ours or not from an origin we trust. */
export function parseHostMessage(event: MessageEvent, allowed: readonly string[]): HostMessage | null {
  if (!isAllowedOrigin(event.origin, allowed)) return null;

  const parsed = hostMessageSchema.safeParse(event.data);
  return parsed.success ? parsed.data : null;
}

/**
 * Send a message to the host.
 *
 * `target` is a concrete origin and there is no default. Making it required is what stops `"*"` from ever
 * being the easy option, and it is why this takes the origin rather than reading it from module state.
 */
export function postToHost(message: WidgetMessage, target: string): void {
  if (target === '' || target === '*') {
    // Refused rather than sent. A wildcard target delivers to whatever is in the parent slot, which is
    // not necessarily the origin that loaded us, and a `resize` is harmless while an `error` carrying a
    // correlation id is not.
    return;
  }
  window.parent.postMessage(message, target);
}

/** The four messages the frame sends, as constructors, so no call site writes a literal type string. */
export const outbound = {
  ready: (): WidgetMessage => ({ type: WIDGET_TO_HOST.ready }),
  resize: (height: number): WidgetMessage => ({ type: WIDGET_TO_HOST.resize, height: Math.ceil(height) }),
  expiring: (): WidgetMessage => ({ type: WIDGET_TO_HOST.expiring }),
  error: (code: string, correlationId?: string): WidgetMessage =>
    correlationId === undefined
      ? { type: WIDGET_TO_HOST.error, code }
      : { type: WIDGET_TO_HOST.error, code, correlationId },
} as const;
